import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLocation } from "@/lib/location";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash, ArrowsClockwise, QrCode, ForkKnife, SquaresFour, Table as TableIcon, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import RoomsTable from "@/pages/RoomsTable";

export default function AdminRooms() {
    const { activeLocationId, requireLocation } = useLocation() || {};
    const [rooms, setRooms] = useState([]);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [view, setView] = useState(() => localStorage.getItem("admin-rooms-view") || "cards");
    const [showCreate, setShowCreate] = useState(false);
    const [viewRoom, setViewRoom] = useState(null);
    // The room currently being edited via the inline pencil button on each
    // card. Null when the edit dialog is closed.
    const [editRoom, setEditRoom] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", floor: "", location: "" });
    const [savingEdit, setSavingEdit] = useState(false);
    // food_orders_enabled defaults to true so the new-room form mirrors the
    // historical behaviour (every new room could take food orders until the
    // admin explicitly disables it).
    const [form, setForm] = useState({ name: "", floor: "1", location: "Main Office", food_orders_enabled: true });
    // Track which rooms are mid-toggle so the switch is debounced — prevents
    // double-clicks from racing the PATCH request.
    const [togglingId, setTogglingId] = useState(null);

    const load = async () => {
        const [r, l, d] = await Promise.all([
            api.get("/rooms"),
            api.get("/locations").catch(() => ({ data: [] })),
            api.get("/departments").catch(() => ({ data: [] })),
        ]);
        setRooms(r.data);
        setLocations(l.data || []);
        setDepartments(d.data || []);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeLocationId]);

    // Persist the chosen view (cards / table) so the operator's preference
    // survives page reloads.
    useEffect(() => { localStorage.setItem("admin-rooms-view", view); }, [view]);

    // The table view dispatches a "rooms:view-qr" custom event when the user
    // clicks the QR icon in a row — we listen and open the existing QR dialog.
    useEffect(() => {
        const handler = (e) => setViewRoom(e.detail);
        window.addEventListener("rooms:view-qr", handler);
        return () => window.removeEventListener("rooms:view-qr", handler);
    }, []);

    const create = async () => {
        if (!form.name) return toast.error("Room name required");
        try {
            await api.post("/rooms", form);
            toast.success("Room created");
            setShowCreate(false);
            setForm({ name: "", floor: "1", location: "Main Office", food_orders_enabled: true });
            load();
        } catch { toast.error("Failed"); }
    };

    const regenerate = async (id) => {
        try {
            const { data } = await api.post(`/rooms/${id}/regenerate-pin`);
            toast.success("PIN regenerated");
            setRooms((p) => p.map((r) => (r.id === id ? data : r)));
            if (viewRoom?.id === id) setViewRoom(data);
        } catch { toast.error("Failed"); }
    };

    const toggleFoodOrders = async (room) => {
        if (togglingId) return;
        // Existing rooms might predate this field — treat undefined as true so
        // the admin always sees the "currently ON" state for legacy rows.
        const next = !(room.food_orders_enabled !== false);
        setTogglingId(room.id);
        // Optimistic update — the switch flips immediately so the admin gets
        // instant feedback. We roll back on failure.
        setRooms((p) => p.map((r) => (r.id === room.id ? { ...r, food_orders_enabled: next } : r)));
        try {
            const { data } = await api.patch(`/rooms/${room.id}`, { food_orders_enabled: next });
            setRooms((p) => p.map((r) => (r.id === room.id ? data : r)));
            toast.success(next ? "Food orders enabled for this room" : "Food orders disabled for this room");
        } catch {
            // Rollback
            setRooms((p) => p.map((r) => (r.id === room.id ? room : r)));
            toast.error("Failed to update");
        } finally {
            setTogglingId(null);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this room?")) return;
        try {
            await api.delete(`/rooms/${id}`);
            toast.success("Deleted");
            load();
        } catch { toast.error("Failed"); }
    };

    // Open the edit dialog for a room — prefills the form with the room's
    // current name/floor/location so the admin only has to change what they
    // want to change.
    const openEdit = (room) => {
        setEditRoom(room);
        setEditForm({
            name: room.name || "",
            floor: room.floor || "",
            location: room.location || "",
        });
    };

    const saveEdit = async () => {
        if (!editRoom) return;
        if (!editForm.name.trim()) { toast.error("Room name required"); return; }
        setSavingEdit(true);
        try {
            const { data } = await api.patch(`/rooms/${editRoom.id}`, {
                name: editForm.name.trim(),
                floor: editForm.floor.trim() || "1",
                location: editForm.location.trim() || "Main Office",
            });
            // Optimistic merge — keep the QR/PIN/food_orders_enabled fields from
            // the server response (which include any backfilled values).
            setRooms((p) => p.map((r) => (r.id === editRoom.id ? data : r)));
            toast.success("Room updated");
            setEditRoom(null);
        } catch {
            toast.error("Failed to update room");
        } finally {
            setSavingEdit(false);
        }
    };

    return (
        <AppShell
            title="Rooms"
            subtitle="Manage room access"
            actions={
                <>
                    <Button
                        onClick={() => requireLocation && requireLocation(() => setShowCreate(true), "a room")}
                        className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                        data-testid="rooms-create-btn"
                    >
                        <Plus size={16} className="mr-1" /> New Room
                    </Button>
                    <Dialog open={showCreate} onOpenChange={setShowCreate}>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Create Room</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label>Name</Label>
                                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Board Room 02" data-testid="room-name-input" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Floor</Label>
                                        <Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} data-testid="room-floor-input" />
                                    </div>
                                    <div>
                                        <Label>Location</Label>
                                        <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="room-location-input" />
                                    </div>
                                </div>
                                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 bg-gray-50 dark:bg-gray-900/40">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <Switch
                                            checked={!!form.food_orders_enabled}
                                            onCheckedChange={(v) => setForm({ ...form, food_orders_enabled: !!v })}
                                            data-testid="room-create-food-toggle"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                                                <ForkKnife size={14} weight="duotone" /> Allow food orders
                                            </div>
                                            <div className="text-[11px] text-gray-500 mt-0.5">
                                                When OFF, this room&apos;s kiosk hides the &quot;Order Food&quot; button. Useful for cabins or meeting rooms where ordering isn&apos;t allowed.
                                            </div>
                                        </div>
                                    </label>
                                </div>
                                <Button onClick={create} className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="room-create-submit-btn">Create</Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </>
            }
        >
            <div className="p-6">
                <Tabs value={view} onValueChange={setView} className="w-full">
                    <TabsList className="mb-4" data-testid="rooms-view-tabs">
                        <TabsTrigger value="cards" className="gap-1.5" data-testid="rooms-view-cards-tab">
                            <SquaresFour size={14} weight="duotone" /> Cards
                        </TabsTrigger>
                        <TabsTrigger value="table" className="gap-1.5" data-testid="rooms-view-table-tab">
                            <TableIcon size={14} weight="duotone" /> Table
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="cards" className="m-0">
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rooms-grid">
                {rooms.map((r) => {
                        // The QR encodes the short URL (e.g. /r/abc1) which
                        // redirects to /room/<pin>. We surface that short form
                        // on the card so people can type it manually if their
                        // phone can't scan the QR.
                        let shortHref = "";
                        let shortLabel = "";
                        try {
                            const u = new URL(r.qr_payload, window.location.origin);
                            shortHref = u.href;
                            shortLabel = `${u.host}${u.pathname}`;
                        } catch {
                            shortHref = r.qr_payload || "";
                            shortLabel = r.qr_payload || "";
                        }
                        return (
                        <Card key={r.id} className="border-gray-200 bg-white p-5" data-testid={`room-card-${r.id}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="label-eyebrow text-gray-500">Floor {r.floor} · {r.location}</div>
                                    <div className="font-display text-lg font-semibold text-gray-900 mt-1">{r.name}</div>
                                </div>
                                <div className="font-mono text-xs px-2 py-1 rounded bg-gray-50 border border-gray-200 text-gray-700">PIN {r.pin}</div>
                            </div>
                            <a
                                href={shortHref}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-3 block font-mono text-[11px] text-[#0055FF] hover:underline truncate"
                                title={shortHref}
                                data-testid={`room-${r.id}-short-url`}
                            >
                                {shortLabel}
                            </a>
                            {/* Per-room food orders toggle. Existing rooms with no
                                stored value are treated as enabled (default), so
                                the switch starts ON until the admin explicitly
                                flips it OFF. */}
                            <div className="mt-3 flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800">
                                <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                    <ForkKnife size={14} weight="duotone" />
                                    Food orders
                                </div>
                                <Switch
                                    checked={r.food_orders_enabled !== false}
                                    disabled={togglingId === r.id}
                                    onCheckedChange={() => toggleFoodOrders(r)}
                                    data-testid={`room-${r.id}-food-toggle`}
                                />
                            </div>
                            <div className="mt-4 flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="text-xs" data-testid={`room-${r.id}-edit-btn`}>
                                    <PencilSimple size={14} className="mr-1" /> Edit
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setViewRoom(r)} className="text-xs" data-testid={`room-${r.id}-view-btn`}>
                                    <QrCode size={14} className="mr-1" /> View QR
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => regenerate(r.id)} className="text-xs" data-testid={`room-${r.id}-regen-btn`}>
                                    <ArrowsClockwise size={14} className="mr-1" /> Regenerate
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-xs text-red-600 hover:bg-red-50 ml-auto" data-testid={`room-${r.id}-delete-btn`}>
                                    <Trash size={14} />
                                </Button>
                            </div>
                        </Card>
                        );
                    })}
                        </div>
                    </TabsContent>

                    <TabsContent value="table" className="m-0">
                        <RoomsTable
                            rooms={rooms}
                            locations={locations}
                            departments={departments}
                            onPatchRoom={async (id, patch) => {
                                // Re-uses the existing optimistic toggleFoodOrders flow when
                                // food_orders_enabled changes; otherwise just PATCHes and refreshes.
                                if ("food_orders_enabled" in patch) {
                                    const r = rooms.find((x) => x.id === id);
                                    if (r) await toggleFoodOrders(r);
                                    return;
                                }
                                try {
                                    const { data } = await api.patch(`/rooms/${id}`, patch);
                                    setRooms((p) => p.map((x) => (x.id === id ? data : x)));
                                } catch { toast.error("Failed to update"); }
                            }}
                            onDeleteRoom={remove}
                            onRegeneratePin={regenerate}
                            onReload={load}
                        />
                    </TabsContent>
                </Tabs>

                <Dialog open={!!viewRoom} onOpenChange={(o) => !o && setViewRoom(null)}>
                    <DialogContent>
                        <DialogHeader><DialogTitle>{viewRoom?.name}</DialogTitle></DialogHeader>
                        {viewRoom && (
                            <div className="space-y-4">
                                <div className="bg-white p-4 border border-gray-200 rounded-lg grid place-items-center">
                                    <img src={viewRoom.qr_image} alt="QR" className="w-64 h-64" />
                                </div>
                                <div className="text-center">
                                    <div className="label-eyebrow">PIN</div>
                                    <div className="font-mono text-4xl font-bold tracking-[0.3em] text-gray-900 dark:text-gray-100 mt-1">{viewRoom.pin}</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-3 text-center">
                                    <div className="label-eyebrow mb-1">Scanning opens</div>
                                    <a
                                        href={viewRoom.qr_payload}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="font-mono text-xs text-[#0055FF] hover:underline break-all"
                                        data-testid={`room-${viewRoom.id}-qr-url`}
                                    >
                                        {viewRoom.qr_payload}
                                    </a>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
                <Dialog open={!!editRoom} onOpenChange={(o) => !o && setEditRoom(null)}>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Edit Room</DialogTitle></DialogHeader>
                        {editRoom && (
                            <div className="space-y-4">
                                <div>
                                    <Label>Name</Label>
                                    <Input
                                        value={editForm.name}
                                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                        placeholder="e.g. Board Room 02"
                                        data-testid="room-edit-name-input"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Floor</Label>
                                        <Input
                                            value={editForm.floor}
                                            onChange={(e) => setEditForm({ ...editForm, floor: e.target.value })}
                                            data-testid="room-edit-floor-input"
                                        />
                                    </div>
                                    <div>
                                        <Label>Location</Label>
                                        <Input
                                            value={editForm.location}
                                            onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                                            data-testid="room-edit-location-input"
                                        />
                                    </div>
                                </div>
                                <div className="text-[11px] text-gray-500">
                                    PIN <span className="font-mono">{editRoom.pin}</span> and QR are unchanged. Use the Regenerate button on the card to rotate the PIN.
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" onClick={() => setEditRoom(null)} className="flex-1" data-testid="room-edit-cancel-btn">Cancel</Button>
                                    <Button
                                        onClick={saveEdit}
                                        disabled={savingEdit}
                                        className="flex-1 bg-[#0055FF] hover:bg-[#0044CC] text-white"
                                        data-testid="room-edit-save-btn"
                                    >
                                        {savingEdit ? "Saving…" : "Save"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </AppShell>
    );
}
